/**
 * CodeInput - a single 6-digit numeric field for emailed verification codes.
 *
 * Deliberately not built on ui/Input: that component's keyboardType union omits
 * 'number-pad' and it exposes no maxLength / autoComplete / textContentType, all
 * of which matter for a one-time code. The onChange handler still strips
 * non-digits because number-pad does not guarantee digit-only input on every
 * Android keyboard and a pasted code can carry spaces.
 */

import React, { useMemo } from 'react';
import { View, TextInput, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';

interface CodeInputProps {
  value: string;
  onChangeText: (text: string) => void;
  label?: string;
  error?: string;
  autoFocus?: boolean;
  testID?: string;
}

export function CodeInput({
  value,
  onChangeText,
  label,
  error,
  autoFocus = false,
  testID,
}: CodeInputProps) {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const handleChange = (text: string) => {
    onChangeText(text.replace(/[^0-9]/g, '').slice(0, 6));
  };

  return (
    <View style={styles.container}>
      {label && <Text style={styles.label}>{label}</Text>}
      <TextInput
        testID={testID}
        style={[styles.input, error ? styles.inputError : undefined]}
        value={value}
        onChangeText={handleChange}
        placeholder="------"
        placeholderTextColor={theme.colors.textTertiary}
        keyboardType="number-pad"
        maxLength={6}
        autoComplete="sms-otp"
        textContentType="oneTimeCode"
        autoFocus={autoFocus}
      />
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      marginBottom: 16,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.colors.text,
      marginBottom: 8,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? 1 : 0,
    },
    input: {
      backgroundColor: theme.colors.backgroundElevated,
      borderRadius: theme.radii.sm,
      paddingHorizontal: 16,
      paddingVertical: 12,
      fontSize: 24,
      letterSpacing: 8,
      textAlign: 'center',
      color: theme.colors.text,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
    },
    inputError: {
      borderColor: theme.colors.error,
    },
    errorText: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.error,
      marginTop: 4,
    },
  });
}
